import { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Users, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, UserMinus, UserPlus, Mail, Copy, X, Coins, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useGetAdminOfficesQuery,
  useGetAdminOfficeQuery,
  useCreateAdminOfficeMutation,
  useUpdateAdminOfficeMutation,
  useDeleteAdminOfficeMutation,
  useAddOfficeMemberMutation,
  useRemoveOfficeMemberMutation,
  useUpdateOfficeMemberRoleMutation,
  useUpdateOfficeSubscriptionMutation,
  useUpdateOfficeCreditsMutation,
  useRefreshOfficeQuotaWindowMutation,
  useExtendOfficeSubscriptionMutation,
  type AdminOffice,
} from "@/redux/api/adminOffices.rtkq";
import { useGetAdminUsersQuery } from "@/redux/api/adminUsers.rtkq";
import { useGetPlansQuery, useRestorePlanMutation } from "@/redux/api/plans.rtkq";
import PlanAssignment from "./PlanAssignment";
import { useGetAppsQuery } from "@/redux/api/apps.rtkq";
import {
  useGetAdminInvitationsQuery,
  useCreateAdminInvitationMutation,
  useCancelAdminInvitationMutation,
} from "@/redux/api/adminInvitations.rtkq";
import { toast } from "sonner";
import EditableCell from "./EditableCell";
import AppCreditsEditor from "./AppCreditsEditor";

type SortField = "name" | "owner" | "members" | "plan" | "billing" | "status" | "credits" | "createdAt";
type SortDirection = "asc" | "desc";

const sumFiniteBalances = (balances: Record<string, number> | undefined): number =>
  Object.values(balances ?? {}).reduce(
    (sum, amount) => sum + (Number.isFinite(amount) ? amount : 0),
    0,
  );

const officeBonusTotal = (office: AdminOffice): number =>
  office.bonusCreditBalance ?? sumFiniteBalances(office.appBonusCreditBalances);

const FREE_PLAN_NAMES = new Set(["free", "free-agent-cloud"]);

const BILLING_STATUS_OPTIONS = [
  { value: "paying", label: "משלם" },
  { value: "trial", label: "ניסיון" },
  { value: "courtesy", label: "courtesy" },
  { value: "none", label: "אין" },
];

const billingLabel = (status?: string): string =>
  BILLING_STATUS_OPTIONS.find((o) => o.value === status)?.label || status || "אין";

const memberRoleClass = (role: string): string =>
  role === "office_admin" ? "text-violet-700" : "text-sky-700";

const MIGRATION_SYSTEM_USER_ID = "00000000-0000-0000-0000-000000000010";

const memberRoleLabel = (role: string): string => (role === "office_admin" ? "מנהל" : "משתמש");

const addMemberOptionLabel = (
  user?: { displayName?: string | null; email: string; offices: { officeId: string; officeName?: string | null }[] },
): string => {
  if (!user) return "";
  const officeLabel = user.offices.length
    ? user.offices.map((office) => office.officeName || office.officeId).join(", ")
    : "לא משויך";
  return `${user.displayName || user.email} — ${officeLabel}`;
};

const isFreeOfficePlan = (office: Pick<AdminOffice, "subscriptionPlan">): boolean =>
  FREE_PLAN_NAMES.has(
    (office.subscriptionPlan?.internalName || "").trim().toLowerCase(),
  );

const formatQuotaWindowEnd = (value?: string | Date | null): string => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
};

const isQuotaWindowExpired = (value?: string | Date | null): boolean => {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
};

const OfficesManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);
  const [draftWindowCredits, setDraftWindowCredits] = useState<Record<string, number>>({});
  const [draftBonusCredits, setDraftBonusCredits] = useState<Record<string, number>>({});
  const [touchedWindowCredits, setTouchedWindowCredits] = useState<Record<string, true>>({});
  const [touchedBonusCredits, setTouchedBonusCredits] = useState<Record<string, true>>({});
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Create office form state
  const [createFormData, setCreateFormData] = useState({
    name: "",
    ownerUserId: "",
    planId: "",
  });

  // Add member form state
  const [addMemberData, setAddMemberData] = useState({
    userId: "",
    role: "office_user",
  });
  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false);
  const [removeWarningUserId, setRemoveWarningUserId] = useState<string | null>(null);

  const {
    data: offices = [],
    isLoading: officesLoading,
    error: officesError,
    refetch: refetchOffices,
  } = useGetAdminOfficesQuery(undefined, { pollingInterval: 60_000 });

  const {
    data: officeDetail,
    isLoading: officeDetailLoading,
  } = useGetAdminOfficeQuery(selectedOfficeId!, { skip: !selectedOfficeId });

  const { data: users = [] } = useGetAdminUsersQuery();
  const { data: plans = [] } = useGetPlansQuery();
  const [restorePlan, { isLoading: isRestoringPlan }] = useRestorePlanMutation();
  const { data: apps = [] } = useGetAppsQuery();

  const [createOffice, { isLoading: isCreating }] = useCreateAdminOfficeMutation();
  const [deleteOffice] = useDeleteAdminOfficeMutation();
  const [addMember, { isLoading: isAddingMember }] = useAddOfficeMemberMutation();
  const [removeMember] = useRemoveOfficeMemberMutation();
  const [updateMemberRole] = useUpdateOfficeMemberRoleMutation();
  const [updateAdminOffice] = useUpdateAdminOfficeMutation();
  const [updateOfficeSubscription] = useUpdateOfficeSubscriptionMutation();
  const [updateOfficeCredits, { isLoading: isSavingCredits }] = useUpdateOfficeCreditsMutation();
  const [refreshOfficeQuotaWindow, { isLoading: isRefreshingWindow }] =
    useRefreshOfficeQuotaWindowMutation();
  const [extendOfficeSubscription, { isLoading: isExtendingPeriod }] =
    useExtendOfficeSubscriptionMutation();

  // Invitations
  const { data: invitations = [] } = useGetAdminInvitationsQuery();
  const [createInvitation, { isLoading: isInviting }] = useCreateAdminInvitationMutation();
  const [cancelInvitation] = useCancelAdminInvitationMutation();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedOffices = useMemo(() => {
    const sorted = [...offices].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case "name":
          aValue = a.name?.toLowerCase() || "";
          bValue = b.name?.toLowerCase() || "";
          break;
        case "owner":
          aValue = a.owner?.displayName?.toLowerCase() || a.owner?.email?.toLowerCase() || "";
          bValue = b.owner?.displayName?.toLowerCase() || b.owner?.email?.toLowerCase() || "";
          break;
        case "members":
          aValue = a.memberCount || 0;
          bValue = b.memberCount || 0;
          break;
        case "plan":
          aValue = a.subscriptionPlan?.name?.toLowerCase() || "";
          bValue = b.subscriptionPlan?.name?.toLowerCase() || "";
          break;
        case "billing":
          aValue = a.billingStatus || "";
          bValue = b.billingStatus || "";
          break;
        case "status":
          aValue = a.subscriptionStatus || "";
          bValue = b.subscriptionStatus || "";
          break;
        case "credits":
          aValue = (a.creditBalance || 0) + officeBonusTotal(a);
          bValue = (b.creditBalance || 0) + officeBonusTotal(b);
          break;
        case "createdAt":
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [offices, sortField, sortDirection]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Helper function to safely format dates
  const formatDate = (date: Date | string | undefined, fallback: string = "—"): string => {
    if (!date) return fallback;

    try {
      const dateObj = date instanceof Date ? date : new Date(date);

      if (isNaN(dateObj.getTime())) {
        return "תאריך לא תקין";
      }

      return dateObj.toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "תאריך לא תקין";
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      active: "default",
      past_due: "destructive",
      cancelled: "secondary",
      expired: "destructive",
      incomplete: "outline",
    } as const;

    const labels = {
      active: "פעיל",
      past_due: "תשלום באיחור",
      cancelled: "מבוטל",
      expired: "פג תוקף",
      incomplete: "לא הושלם",
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || "secondary"}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    );
  };


  // Handlers
  const handleCreateOffice = async () => {
    if (!createFormData.name || !createFormData.ownerUserId) {
      toast.error("נא למלא שם ולבחור בעלים");
      return;
    }

    try {
      await createOffice({
        name: createFormData.name,
        ownerUserId: createFormData.ownerUserId,
        planId: createFormData.planId || undefined,
      }).unwrap();
      toast.success("המשרד נוצר בהצלחה");
      setCreateFormData({ name: "", ownerUserId: "", planId: "" });
      setIsCreateDialogOpen(false);
    } catch (error) {
      console.error("Failed to create office:", error);
      toast.error("שגיאה ביצירת המשרד");
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isRefreshConfirmOpen, setIsRefreshConfirmOpen] = useState(false);

  const handleDeleteOffice = async (deleteMembers: boolean) => {
    if (!deleteConfirmId) return;
    try {
      await deleteOffice({ id: deleteConfirmId, deleteMembers }).unwrap();
      toast.success(deleteMembers ? "המשרד והמשתמשים נמחקו בהצלחה" : "המשרד נמחק בהצלחה");
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Failed to delete office:", error);
      toast.error("שגיאה במחיקת המשרד");
    }
  };

  const openMembersDialog = (officeId: string) => {
    setIsCreditsDialogOpen(false);
    setSelectedOfficeId(officeId);
    setAddMemberData({ userId: "", role: "office_user" });
    setIsMembersDialogOpen(true);
  };

  const openCreditsDialog = (officeId: string) => {
    setIsMembersDialogOpen(false);
    setSelectedOfficeId(officeId);
    setTouchedWindowCredits({});
    setTouchedBonusCredits({});
    setIsCreditsDialogOpen(true);
  };

  const appNameById = useMemo(() => {
    const names: Record<string, string> = {};
    for (const app of apps) {
      if (app.appIdentifier) names[app.appIdentifier] = app.name;
      if (app.appId) names[app.appId] = app.name;
    }
    return names;
  }, [apps]);

  useEffect(() => {
    if (!isCreditsDialogOpen || !officeDetail) return;
    const windowBalances: Record<string, number> = { ...(officeDetail.appCreditBalances || {}) };
    const bonusBalances: Record<string, number> = { ...(officeDetail.appBonusCreditBalances || {}) };
    const includedApps = officeDetail.subscriptionPlan?.includedApps ?? [];
    for (const app of includedApps) {
      const appId = app.appIdentifier;
      if (!appId) continue;
      if (!(appId in windowBalances)) windowBalances[appId] = 0;
      if (!(appId in bonusBalances)) bonusBalances[appId] = 0;
    }
    for (const appId of Object.keys(windowBalances)) {
      if (!(appId in bonusBalances)) bonusBalances[appId] = 0;
    }
    for (const appId of Object.keys(bonusBalances)) {
      if (!(appId in windowBalances)) windowBalances[appId] = 0;
    }
    setDraftWindowCredits(windowBalances);
    setDraftBonusCredits(bonusBalances);
    setTouchedWindowCredits({});
    setTouchedBonusCredits({});
  }, [isCreditsDialogOpen, officeDetail]);

  const draftWindowTotal = useMemo(
    () =>
      Object.values(draftWindowCredits).reduce(
        (sum, amount) => sum + (Number.isFinite(amount) ? amount : 0),
        0,
      ),
    [draftWindowCredits],
  );

  const draftBonusTotal = useMemo(
    () =>
      Object.values(draftBonusCredits).reduce(
        (sum, amount) => sum + (Number.isFinite(amount) ? amount : 0),
        0,
      ),
    [draftBonusCredits],
  );

  const creditEditorRows = useMemo(() => {
    const appIds = Array.from(
      new Set([...Object.keys(draftWindowCredits), ...Object.keys(draftBonusCredits)]),
    );
    return appIds.map((appId) => ({
      id: appId,
      name: appNameById[appId] || appId,
      subtitle: appId,
      fields: [
        { key: "window", value: draftWindowCredits[appId] ?? 0, min: 0 },
        { key: "bonus", value: draftBonusCredits[appId] ?? 0, min: 0 },
      ],
    }));
  }, [appNameById, draftBonusCredits, draftWindowCredits]);

  const handleSaveOfficeCredits = async () => {
    if (!selectedOfficeId || !officeDetail) return;
    const existingBonus = officeDetail.appBonusCreditBalances || {};
    const existingWindow = officeDetail.appCreditBalances || {};
    const appCreditBalances: Record<string, number> = {};
    const windowCreditBalances: Record<string, number> = {};
    for (const [appId, amount] of Object.entries(draftBonusCredits)) {
      if (appId in existingBonus || touchedBonusCredits[appId]) {
        appCreditBalances[appId] = amount;
      }
    }
    for (const [appId, amount] of Object.entries(draftWindowCredits)) {
      if (appId in existingWindow || touchedWindowCredits[appId]) {
        windowCreditBalances[appId] = amount;
      }
    }
    if (
      Object.keys(appCreditBalances).length === 0 &&
      Object.keys(windowCreditBalances).length === 0
    ) {
      setIsCreditsDialogOpen(false);
      return;
    }
    try {
      await updateOfficeCredits({
        officeId: selectedOfficeId,
        ...(Object.keys(appCreditBalances).length > 0 ? { appCreditBalances } : {}),
        ...(Object.keys(windowCreditBalances).length > 0 ? { windowCreditBalances } : {}),
      }).unwrap();
      toast.success("יתרות הקרדיטים עודכנו");
      setIsCreditsDialogOpen(false);
    } catch (error) {
      console.error("Failed to update office credits:", error);
      toast.error("שגיאה בעדכון הקרדיטים");
    }
  };

  const handleRefreshQuotaWindow = async () => {
    if (!selectedOfficeId || !officeDetail) return;
    try {
      const result = await refreshOfficeQuotaWindow({ officeId: selectedOfficeId }).unwrap();
      setIsRefreshConfirmOpen(false);
      toast.success(
        result.quotaWindowEnd
          ? `החלון חודש עד ${formatQuotaWindowEnd(result.quotaWindowEnd)}`
          : "החלון חודש",
      );
    } catch (error) {
      console.error("Failed to refresh quota window:", error);
      toast.error("שגיאה ברענון חלון הקצובה");
    }
  };

  const handleToggleAutoRenew = async (officeId: string, enabled: boolean) => {
    try {
      await updateAdminOffice({ id: officeId, updates: { autoRenew: enabled } }).unwrap();
      toast.success(enabled ? "חידוש אוטומטי הופעל" : "חידוש אוטומטי כובה");
    } catch (error) {
      console.error("Failed to update auto-renew:", error);
      toast.error("שגיאה בעדכון חידוש אוטומטי");
    }
  };

  const handleExtendPaidPeriod = async (officeId: string) => {
    try {
      const result = await extendOfficeSubscription({ officeId, months: 1 }).unwrap();
      toast.success(
        result.subscriptionCurrentPeriodEnd
          ? `המנוי הוארך עד ${formatQuotaWindowEnd(result.subscriptionCurrentPeriodEnd)}`
          : "המנוי הוארך בחודש",
      );
    } catch (error) {
      console.error("Failed to extend subscription:", error);
      toast.error("שגיאה בהארכת המנוי");
    }
  };

  const otherOfficesFor = (userId: string) => {
    const member = users.find((user) => user.id === userId);
    return (member?.offices ?? []).filter((office) => office.officeId !== selectedOfficeId);
  };

  const handleAddMember = () => {
    if (!selectedOfficeId || !addMemberData.userId) {
      toast.error("נא לבחור משתמש");
      return;
    }
    if (otherOfficesFor(addMemberData.userId).length > 0) {
      setIsMoveConfirmOpen(true);
      return;
    }
    void submitAddMember();
  };

  const submitAddMember = async () => {
    if (!selectedOfficeId || !addMemberData.userId) return;
    const moving = otherOfficesFor(addMemberData.userId).length > 0;

    try {
      await addMember({
        officeId: selectedOfficeId,
        userId: addMemberData.userId,
        role: addMemberData.role,
      }).unwrap();
      toast.success(moving ? "המשתמש הועבר למשרד" : "המשתמש נוסף בהצלחה");
      setAddMemberData({ userId: "", role: "office_user" });
      setIsMoveConfirmOpen(false);
    } catch (error) {
      console.error("Failed to add member:", error);
      toast.error("שגיאה בהוספת משתמש");
    }
  };

  const handleRemoveMember = (userId: string) => {
    if (!selectedOfficeId) return;
    if (otherOfficesFor(userId).length === 0) {
      setRemoveWarningUserId(userId);
      return;
    }
    void submitRemoveMember(userId);
  };

  const submitRemoveMember = async (userId: string) => {
    if (!selectedOfficeId) return;

    try {
      await removeMember({ officeId: selectedOfficeId, userId }).unwrap();
      toast.success("המשתמש הוסר בהצלחה");
      setRemoveWarningUserId(null);
    } catch (error) {
      console.error("Failed to remove member:", error);
      toast.error("שגיאה בהסרת משתמש");
    }
  };

  const handleUpdateMemberRole = async (userId: string, newRole: string) => {
    if (!selectedOfficeId) return;

    try {
      await updateMemberRole({ officeId: selectedOfficeId, userId, role: newRole }).unwrap();
      toast.success("תפקיד המשתמש עודכן בהצלחה");
    } catch (error) {
      console.error("Failed to update member role:", error);
      toast.error("שגיאה בעדכון תפקיד");
    }
  };

  // Users not yet members of the selected office (for add member dropdown)
  const availableUsers = useMemo(() => {
    const memberIds = new Set((officeDetail?.members || []).map((m) => m.userId));
    return users.filter((u) => u.id !== MIGRATION_SYSTEM_USER_ID && !memberIds.has(u.id));
  }, [users, officeDetail]);

  const officesLeftBehind = otherOfficesFor(addMemberData.userId);
  const movingMember = officesLeftBehind.length > 0;

  if (officesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">טוען נתונים...</div>
      </div>
    );
  }

  if (officesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">שגיאה בטעינת המשרדים</div>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">ניהול משרדים</h3>
          <p className="text-sm text-slate-600">צפייה, יצירה וניהול משרדים וחברים</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setIsInviteDialogOpen(true)}
          >
            <Mail className="h-4 w-4 ml-2" />
            הזמן משתמש
          </Button>
          <Button
            className="bg-gradient-to-r from-sky-400 to-indigo-500 text-white"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4 ml-2" />
            משרד חדש
          </Button>
        </div>
      </div>

      {/* Offices Table */}
      <Card className="min-w-0 overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>משרדים במערכת</CardTitle>
              <CardDescription>רשימת כל המשרדים הרשומים במערכת</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchOffices()}
              disabled={officesLoading}
              className="border-slate-200 text-slate-800 hover:bg-slate-100"
            >
              <RefreshCw className="h-4 w-4 ml-2" />
              {officesLoading ? "מרענן..." : "רענן"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {offices.length === 0 ? (
            <div className="text-center py-8 text-slate-600">אין משרדים במערכת. צור משרד ראשון כדי להתחיל.</div>
          ) : (
            <Table
              containerClassName="overflow-x-hidden"
              className="table-fixed [&_th]:whitespace-normal [&_td]:overflow-hidden [&_td]:whitespace-normal [&_td]:break-words [&_button]:max-w-full [&_button]:whitespace-normal [&_select]:w-full [&_select]:min-w-0 [&_select]:max-w-full"
            >
              <colgroup>
                <col className="w-[13%]" />
                <col className="w-[14%]" />
                <col className="w-[5%]" />
                <col className="w-[12%]" />
                <col className="w-[7%]" />
                <col className="w-[7%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("name")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      שם המשרד
                      {getSortIcon("name")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("owner")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      בעלים
                      {getSortIcon("owner")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("members")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      חברים
                      {getSortIcon("members")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("plan")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      תוכנית מנוי
                      {getSortIcon("plan")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("billing")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      בילינג
                      {getSortIcon("billing")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("status")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      סטטוס
                      {getSortIcon("status")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">תוקף מנוי</TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("credits")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      קרדיטים
                      {getSortIcon("credits")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort("createdAt")}
                      className="h-auto w-full justify-center whitespace-normal px-1 text-center"
                    >
                      תאריך יצירה
                      {getSortIcon("createdAt")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">פעולות</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOffices.map((office: AdminOffice) => (
                  <TableRow
                    key={office.id}
                    className={office.autoRenew ? "bg-emerald-50 hover:bg-emerald-100" : undefined}
                  >
                    <TableCell className="text-center py-3">
                      <EditableCell
                        className="w-full justify-center"
                        value={office.name}
                        onSave={async (val) => { await updateAdminOffice({ id: office.id, updates: { name: val as string } }).unwrap(); }}
                        type="text"
                        displayValue={
                          <span
                            className="font-medium hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              openCreditsDialog(office.id);
                            }}
                          >
                            {office.name}
                          </span>
                        }
                      />
                      <div className="break-all text-xs text-muted-foreground font-mono">{office.id}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const members = (users || [])
                          .flatMap((user) => {
                            const membership = user.offices?.find((item) => item.officeId === office.id);
                            if (!membership) return [];
                            return [{ user, role: membership.role }];
                          })
                          .sort((a, b) =>
                            (a.user.displayName || a.user.email).localeCompare(
                              b.user.displayName || b.user.email,
                            ),
                          );
                        const shown = members.find((member) => member.role === "office_admin") ?? members[0];
                        if (!shown) {
                          return <span className="text-muted-foreground">—</span>;
                        }
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-2 rounded-md px-2 py-1 text-xs hover:bg-muted"
                              >
                                <span className="font-medium">{shown.user.displayName || shown.user.email}</span>
                                <span key={shown.role} className={memberRoleClass(shown.role)}>
                                  {memberRoleLabel(shown.role)}
                                </span>
                                {members.length > 1 && (
                                  <span className="text-muted-foreground">+{members.length - 1}</span>
                                )}
                                <ChevronDown className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="center">
                              <div className="flex flex-col gap-1">
                                {members.map(({ user, role }) => (
                                  <div
                                    key={user.id}
                                    className="flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
                                  >
                                    <span className="truncate font-medium">{user.displayName || user.email}</span>
                                    <span key={role} className={memberRoleClass(role)}>
                                      {memberRoleLabel(role)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline">{office.memberCount}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <PlanAssignment
                        className="items-center"
                        planId={office.subscriptionPlan?.id}
                        planName={office.subscriptionPlan?.name}
                        planActive={office.subscriptionPlan?.isActive}
                        plans={plans}
                        restoring={isRestoringPlan}
                        onRestore={(planId) => {
                          void restorePlan({ planId }).unwrap();
                        }}
                        onChange={(planId) => {
                          void updateOfficeSubscription({ officeId: office.id, planId }).unwrap();
                        }}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <EditableCell
                        className="w-full justify-center"
                        value={office.billingStatus || "none"}
                        onSave={async (val) => { await updateAdminOffice({ id: office.id, updates: { billingStatus: val as string } }).unwrap(); }}
                        type="select"
                        options={BILLING_STATUS_OPTIONS}
                        displayValue={<Badge variant="outline">{billingLabel(office.billingStatus)}</Badge>}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <EditableCell
                        className="w-full justify-center"
                        value={office.subscriptionStatus}
                        onSave={async (val) => { await updateAdminOffice({ id: office.id, updates: { subscriptionStatus: val as string } }).unwrap(); }}
                        type="select"
                        options={[
                          { value: "active", label: "פעיל" },
                          { value: "cancelled", label: "מבוטל" },
                          { value: "expired", label: "פג תוקף" },
                        ]}
                        displayValue={getStatusBadge(office.subscriptionStatus)}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      {isFreeOfficePlan(office) ? (
                        <span className="text-sm text-muted-foreground">ללא תוקף</span>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <span className="font-mono text-sm">
                            {formatQuotaWindowEnd(office.subscriptionCurrentPeriodEnd)}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={isExtendingPeriod}
                            onClick={() => void handleExtendPaidPeriod(office.id)}
                          >
                            +חודש
                          </Button>
                          <label className="flex items-center gap-1.5 text-[11px] text-emerald-800">
                            <Checkbox
                              checked={office.autoRenew === true}
                              onCheckedChange={(checked) => {
                                void handleToggleAutoRenew(office.id, checked === true);
                              }}
                              aria-label="חידוש אוטומטי"
                            />
                            חידוש אוטומטי
                          </label>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="mx-auto h-auto max-w-full gap-2 whitespace-normal py-1.5"
                        onClick={() => openCreditsDialog(office.id)}
                      >
                        <Coins className="h-4 w-4 shrink-0" />
                        <span className="flex flex-col items-center leading-tight">
                          <span className="font-mono">
                            {office.creditBalance?.toLocaleString()}
                          </span>
                          <span className="text-[11px] font-normal text-muted-foreground">
                            +{officeBonusTotal(office).toLocaleString()} בונוס
                          </span>
                          <span
                            className={
                              isQuotaWindowExpired(office.quotaWindowEnd)
                                ? "text-[11px] font-normal text-destructive"
                                : "text-[11px] font-normal text-muted-foreground"
                            }
                          >
                            עד {formatQuotaWindowEnd(office.quotaWindowEnd)}
                          </span>
                        </span>
                      </Button>
                    </TableCell>
                    <TableCell className="text-center text-xs text-slate-600">
                      {formatDate(office.createdAt)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          onClick={() => openCreditsDialog(office.id)}
                          title="קרדיטים לפי אפליקציה"
                        >
                          <Coins className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          onClick={() => openMembersDialog(office.id)}
                          title="צפייה בחברים"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteConfirmId(office.id)}
                          title="מחיקת משרד"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Office Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>יצירת משרד חדש</DialogTitle>
            <DialogDescription>מלא את הפרטים למשרד החדש</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="office-name">שם המשרד</Label>
              <Input
                id="office-name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                placeholder="שם המשרד"
              />
            </div>
            <div>
              <Label htmlFor="office-owner">בעלים</Label>
              <Select
                value={createFormData.ownerUserId}
                onValueChange={(value) => setCreateFormData({ ...createFormData, ownerUserId: value })}
              >
                <SelectTrigger id="office-owner">
                  <SelectValue placeholder="בחר בעלים" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.displayName || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="office-plan">תוכנית מנוי</Label>
              <Select
                value={createFormData.planId}
                onValueChange={(value) => setCreateFormData({ ...createFormData, planId: value })}
              >
                <SelectTrigger id="office-plan">
                  <SelectValue placeholder="בחר תוכנית (אופציונלי)" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.planId} value={plan.planId!}>
                      {plan.internalName} - ₪{plan.priceMonthly}/חודש
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              ביטול
            </Button>
            <Button onClick={handleCreateOffice} disabled={isCreating}>
              {isCreating ? "יוצר..." : "צור משרד"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Invitations */}
      {invitations.filter((i) => i.status === "pending").length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>הזמנות ממתינות</CardTitle>
            <CardDescription>הזמנות שנשלחו וטרם אושרו</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">דוא"ל</TableHead>
                  <TableHead className="text-right">משרד</TableHead>
                  <TableHead className="text-right">סטטוס</TableHead>
                  <TableHead className="text-right">תאריך</TableHead>
                  <TableHead className="text-right">קישור</TableHead>
                  <TableHead className="text-right">פעולות</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations
                  .filter((i) => i.status === "pending")
                  .map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="text-right font-medium">{inv.email}</TableCell>
                      <TableCell className="text-right">{inv.officeName || "משרד חדש"}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">ממתין</Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">
                        {new Date(inv.createdAt).toLocaleDateString("he-IL")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(inv.acceptUrl);
                            toast.success("הקישור הועתק");
                          }}
                        >
                          <Copy className="h-3 w-3 ml-1" />
                          העתק
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={async () => {
                            await cancelInvitation(inv.id);
                            toast.success("ההזמנה בוטלה");
                          }}
                        >
                          <X className="h-3 w-3 ml-1" />
                          בטל
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Invite User Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>הזמנת משתמש חדש</DialogTitle>
            <DialogDescription>
              שלח הזמנה למשתמש ליצירת משרד חדש. יישלח קישור הזמנה שניתן לשתף.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>דוא"ל</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                dir="ltr"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
              ביטול
            </Button>
            <Button
              disabled={!inviteEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail) || isInviting}
              onClick={async () => {
                try {
                  const result = await createInvitation({
                    email: inviteEmail,
                    createOffice: true,
                  }).unwrap();

                  switch (result.type) {
                    case "invitation_created":
                      await navigator.clipboard.writeText(result.acceptUrl);
                      toast.success("ההזמנה נוצרה ונשלחה — הקישור הועתק ללוח");
                      break;
                    case "office_assigned":
                      toast.success(
                        "המשרד נוצר עבור המשתמש הקיים והודעה נשלחה",
                      );
                      break;
                    case "already_exists_pending":
                      await navigator.clipboard.writeText(result.acceptUrl);
                      toast.info("הזמנה קיימת — נשלחה מחדש");
                      break;
                    case "already_exists_assigned":
                      toast.info("המשתמש כבר מנהל משרד — שלחנו תזכורת");
                      break;
                  }

                  refetchOffices();
                  setInviteEmail("");
                  setIsInviteDialogOpen(false);
                } catch (err: any) {
                  toast.error(err?.data?.message || "שגיאה ביצירת ההזמנה");
                }
              }}
            >
              {isInviting ? "שולח..." : "שלח הזמנה"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-app credits Dialog */}
      <Dialog open={isCreditsDialogOpen} onOpenChange={setIsCreditsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" dir="rtl">
          <DialogHeader>
            <DialogTitle>קרדיטים לפי אפליקציה</DialogTitle>
            <DialogDescription>
              יתרת החלון של {officeDetail?.name || "—"} מתחדשת לפי התוכנית. בונוס ידני לא נמחק באיפוס החלון.
            </DialogDescription>
          </DialogHeader>

          {officeDetailLoading || !officeDetail || officeDetail.id !== selectedOfficeId ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-slate-600">טוען יתרות...</div>
            </div>
          ) : (
            <div className="space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border bg-muted/40 px-3 py-2">
                  <div className="text-xs text-muted-foreground">יתרת חלון</div>
                  <div className="font-mono text-lg">{draftWindowTotal.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">הסכום שמוצג בטבלת המשרדים</div>
                </div>
                <div className="rounded-lg border bg-muted/40 px-3 py-2">
                  <div className="text-xs text-muted-foreground">סכום בונוס</div>
                  <div className="font-mono text-lg">{draftBonusTotal.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">לא נמחק כשהחלון מתחדש</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-3 py-2">
                <div>
                  {isFreeOfficePlan(officeDetail) ? (
                    <>
                      <div className="text-xs text-muted-foreground">תוקף מנוי</div>
                      <div className="text-lg">ללא תוקף</div>
                      <div className="text-xs text-muted-foreground">תוכנית Free אינה מוגבלת בזמן</div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-muted-foreground">תוקף מנוי בתשלום</div>
                      <div className="font-mono text-lg">
                        {formatQuotaWindowEnd(officeDetail.subscriptionCurrentPeriodEnd)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        בתום התקופה המשרד יישאר פעיל ויעבור ל-Free
                      </div>
                    </>
                  )}
                </div>
                {!isFreeOfficePlan(officeDetail) && (
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2 shrink-0"
                    disabled={isExtendingPeriod}
                    onClick={() => void handleExtendPaidPeriod(officeDetail.id)}
                  >
                    +חודש
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-3 py-2">
                <div>
                  <div className="text-xs text-muted-foreground">
                    סיום חלון {officeDetail.subscriptionPlan?.quotaWindowDays ?? officeDetail.quotaWindowDays ?? 3} ימים
                  </div>
                  <div
                    className={
                      isQuotaWindowExpired(officeDetail.quotaWindowEnd)
                        ? "font-mono text-lg text-destructive"
                        : "font-mono text-lg"
                    }
                  >
                    {formatQuotaWindowEnd(officeDetail.quotaWindowEnd)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {isQuotaWindowExpired(officeDetail.quotaWindowEnd)
                      ? "פג תוקף — יתחדש בשימוש הבא או ברענון ידני"
                      : "מתחדש אוטומטית אחרי תאריך הסיום"}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 shrink-0"
                  onClick={() => setIsRefreshConfirmOpen(true)}
                  disabled={isRefreshingWindow || officeDetailLoading}
                >
                  <RefreshCw className={isRefreshingWindow ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                  {isRefreshingWindow ? "מרענן..." : "רענון חלון"}
                </Button>
              </div>
              <AppCreditsEditor
                rows={creditEditorRows}
                fieldLabels={{ window: "יתרת חלון", bonus: "בונוס" }}
                onChange={(appId, field, value) => {
                  if (field === "bonus") {
                    setDraftBonusCredits((prev) => ({ ...prev, [appId]: value }));
                    setTouchedBonusCredits((prev) => ({ ...prev, [appId]: true }));
                    return;
                  }
                  setDraftWindowCredits((prev) => ({ ...prev, [appId]: value }));
                  setTouchedWindowCredits((prev) => ({ ...prev, [appId]: true }));
                }}
                emptyText="אין יתרות לפי אפליקציה"
              />
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsCreditsDialogOpen(false)}>
              ביטול
            </Button>
            <Button
              onClick={() => void handleSaveOfficeCredits()}
              disabled={isSavingCredits || officeDetailLoading || !officeDetail || officeDetail.id !== selectedOfficeId}
            >
              {isSavingCredits ? "שומר..." : "שמירה"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>חברי משרד</DialogTitle>
            <DialogDescription>
              ניהול חברים עבור {officeDetail?.name || "—"}
            </DialogDescription>
          </DialogHeader>

          {officeDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-slate-600">טוען חברים...</div>
            </div>
          ) : (
            <div className="min-w-0 space-y-6 overflow-y-auto">
              {/* Current Members */}
              {officeDetail?.members && officeDetail.members.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">חברים נוכחיים</Label>
                  <div className="border rounded-lg divide-y overflow-y-auto max-h-[40vh]">
                    {officeDetail.members.map((member) => (
                      <div key={member.userId} className="flex min-w-0 items-center justify-between gap-2 p-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-sm">{member.displayName || member.email}</div>
                          <div className="truncate text-xs text-muted-foreground">{member.email}</div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <Select
                            key={`${member.userId}-${member.role}`}
                            value={member.role}
                            onValueChange={(newRole) => handleUpdateMemberRole(member.userId, newRole)}
                          >
                            <SelectTrigger className={`w-[110px] h-8 text-xs ${memberRoleClass(member.role)}`}>
                              <SelectValue>
                                <span key={member.role}>{memberRoleLabel(member.role)}</span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="office_admin" className="text-violet-700">מנהל</SelectItem>
                              <SelectItem value="office_user" className="text-sky-700">משתמש</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMember(member.userId)}
                            className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                            title="הסר חבר"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-slate-600">
                  אין חברים במשרד זה.
                </div>
              )}

              {/* Add Member Section */}
              <div className="space-y-3 border-t pt-4">
                <Label className="text-sm font-medium">הוספת חבר חדש</Label>
                <div className="flex min-w-0 flex-col gap-2">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
                    <div className="min-w-0 flex-1">
                      <Label htmlFor="add-member-user" className="text-xs text-muted-foreground">משתמש</Label>
                      <Select
                        value={addMemberData.userId}
                        onValueChange={(value) => setAddMemberData({ ...addMemberData, userId: value })}
                      >
                        <SelectTrigger id="add-member-user" className="w-full min-w-0">
                          <SelectValue placeholder="בחר משתמש">
                            {addMemberData.userId && (
                              <span key={addMemberData.userId} className="truncate">
                                {addMemberOptionLabel(availableUsers.find((user) => user.id === addMemberData.userId))}
                              </span>
                            )}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {availableUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {addMemberOptionLabel(user)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full shrink-0 sm:w-[140px]">
                      <Label htmlFor="add-member-role" className="text-xs text-muted-foreground">תפקיד</Label>
                      <Select
                        value={addMemberData.role}
                        onValueChange={(value) => setAddMemberData({ ...addMemberData, role: value })}
                      >
                        <SelectTrigger id="add-member-role" className="w-full">
                          <SelectValue>
                            <span key={addMemberData.role}>
                              {addMemberData.role === "office_admin" ? "מנהל משרד" : "משתמש"}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="office_admin">מנהל משרד</SelectItem>
                          <SelectItem value="office_user">משתמש</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleAddMember}
                    disabled={isAddingMember || !addMemberData.userId}
                    className="h-9 w-full sm:w-auto sm:self-start"
                  >
                    <UserPlus className="h-4 w-4 ml-1" />
                    {isAddingMember ? (movingMember ? "מעביר..." : "מוסיף...") : movingMember ? "העבר" : "הוסף"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMembersDialogOpen(false)}>
              סגור
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isMoveConfirmOpen} onOpenChange={setIsMoveConfirmOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>העברת משתמש למשרד</AlertDialogTitle>
            <AlertDialogDescription>
              המשתמש ינותק מ-{officesLeftBehind.map((office) => office.officeName || office.officeId).join(", ")}.
              קריאות MCP יחויבו מהמשרד הזה.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void submitAddMember();
              }}
              disabled={isAddingMember}
            >
              {isAddingMember ? "מעביר..." : "העבר"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={removeWarningUserId !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveWarningUserId(null);
        }}
      >
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>ניתוק משתמש</AlertDialogTitle>
            <AlertDialogDescription>
              אין למשתמש משרד אחר. חיבור ה-MCP ינותק עד שיוצמד למשרד.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                if (removeWarningUserId) void submitRemoveMember(removeWarningUserId);
              }}
            >
              נתק
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isRefreshConfirmOpen} onOpenChange={setIsRefreshConfirmOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>רענון חלון קצובה</AlertDialogTitle>
            <AlertDialogDescription>
              יתרות החלון יתמלאו מחדש לפי התוכנית וייפתח חלון חדש. בונוס ידני לא יימחק.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse sm:justify-start">
            <AlertDialogCancel>ביטול</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleRefreshQuotaWindow();
              }}
              disabled={isRefreshingWindow}
            >
              {isRefreshingWindow ? "מרענן..." : "רענון חלון"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Office Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>מחיקת משרד</DialogTitle>
            <DialogDescription>
              בחר את אופן המחיקה. פעולה זו לא ניתנת לביטול.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Button
              variant="outline"
              className="w-full justify-start text-right h-auto py-3"
              onClick={() => handleDeleteOffice(false)}
            >
              <div>
                <div className="font-medium">מחק משרד בלבד</div>
                <div className="text-xs text-muted-foreground">המשתמשים יישארו במערכת אך יוסרו מהמשרד</div>
              </div>
            </Button>
            <Button
              variant="destructive"
              className="w-full justify-start text-right h-auto py-3"
              onClick={() => handleDeleteOffice(true)}
            >
              <div>
                <div className="font-medium">מחק משרד + כל המשתמשים</div>
                <div className="text-xs text-red-200">המשתמשים יימחקו לצמיתות כולל חשבון Firebase</div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficesManagement;
